import { _isNotBlank, _notNil } from '@/littledash';
import type {
  AgentActivityMetric,
  AgentExecutionApiId,
  AgentSessionStatus,
  AgentStreamEventType,
  AgentStreamEventV1,
} from '@/model/Agent.model';
import { TERMINAL_SESSION_STATUSES } from '@/model/Agent.model';

/**
 * The stream event types the Activity feed ingests — the server's **narrative** channel (see the
 * channel note on `AgentStreamEventV1` in `overwatch-common`). Everything else on the stream is
 * telemetry: `tool_call_*`, `text_delta`, `thinking`, and the execution lifecycle events exist for
 * audit and machinery, and are deliberately NOT rendered here.
 *
 * That boundary is the whole point. The feed used to ingest `tool_call_start`/`tool_call_end` and
 * render `tool_name` verbatim, which is why a researcher saw `getAnimalsForStudy` where they needed
 * "120 animals" — and saw it twice, because a subagent re-probing the roster is a real tool call
 * that a display layer has no licence to hide. Agents now declare the steps they want narrated, so
 * this list should only ever gain event types that a researcher would recognise.
 *
 * `thinking` is excluded for a second reason beyond channel: a thinking row has no completion
 * signal, so it would spin forever. A declared phase resolves.
 */
export const ACTIVITY_EVENT_TYPES = [
  'phase_transition',
  'subagent_started',
  'subagent_completed',
  'subagent_failed',
] as const satisfies ReadonlyArray<AgentStreamEventType>;

/**
 * A row's progress. `resolved` is the neutral "ended while still open" state — the session went
 * terminal before this step reported completion — and is distinct from `failed`, which is a step
 * that explicitly reported an error. Conflating them would tick or grey out a genuine failure.
 */
export type ActivityRowState = 'running' | 'done' | 'resolved' | 'failed';

/**
 * One numeric fact shown beneath a row's title. `key` is an identifier; the UI supplies the word.
 *
 * Aliased from the generated schema rather than hand-declared: these values arrive on the wire as
 * `AgentActivityMetric`, and a local interface that merely happened to have the same shape would keep
 * compiling if the server added or renamed a field, failing later at some unrelated call site instead.
 * The alias keeps the module's local vocabulary (`ActivityRow`, `ActivityRowState`, `ActivityMetric`)
 * while making drift a compile error here.
 */
export type ActivityMetric = AgentActivityMetric;

/**
 * A single Activity row.
 *
 * `activityKey` is the server's identifier (a phase key such as `load_animals`, or a subagent name
 * such as `measurementAnalysis`) and is NOT display text — the panel resolves it through its copy
 * table. Keeping the raw key here means this module stays pure derivation and all wording lives with
 * the layout.
 *
 * `key` is the stable React key, scoped to the execution so the same phase or subagent in a later
 * execution of one session gets its own row.
 */
export type ActivityRow =
  | {
      kind: 'phase';
      key: string;
      activityKey: string;
      state: ActivityRowState;
      metrics: ReadonlyArray<ActivityMetric>;
    }
  | {
      kind: 'subagent';
      key: string;
      activityKey: string;
      state: ActivityRowState;
      /**
       * The key of the phase row this ran inside, so the panel can nest it. Undefined when the event
       * carried no phase — events persisted before the server sent one, which replay flat.
       */
      parentKey?: string;
      /** Counts the unit reported, shown before its duration. Empty for a unit with none. */
      metrics: ReadonlyArray<ActivityMetric>;
      durationMs?: number;
      /**
       * The subagent's own error message, present only on a `failed` row. Carried so the panel can
       * say *why* — a Bedrock throttle (retry the run) and malformed study data (don't) are
       * otherwise the same red mark, with the reason reachable only by querying
       * `agent_execution_events`.
       */
      error?: string;
    };

/** Ordered rows keyed by their stable React key. Map insertion order == first-appearance order. */
export type ActivityRowMap = Map<string, ActivityRow>;

/**
 * Folds a single stream event into the accumulating row map, in place, and reports whether it
 * actually changed anything (so callers can skip re-notifying subscribers on a no-op). This is the
 * one place event → row logic lives; {@link AgentActivityEngine} applies events through it.
 *
 * Both row kinds key on `{kind}:{execution_api_id}:{name}`. Subagents need no server-issued
 * `subagent_call_id`: each subagent is a singleton the agent adds at most once per run, so its name
 * is already unique within an execution — the same execution-scoped-name scheme phases use.
 *
 * Metrics arrive on the `completed` transition, not on `started`, so a completion REPLACES the row
 * with one carrying them rather than only flipping state. A replace rather than a spread because a
 * phase row holds nothing else worth preserving; adding a field to that arm means revisiting this.
 *
 * Terminal resolution (running → resolved) is NOT applied here — it depends on session status and is
 * a final pass in {@link toActivitySnapshot} — so the stored map only ever holds
 * running/done/failed.
 */
export const applyActivityEvent = (rows: ActivityRowMap, event: AgentStreamEventV1): boolean => {
  switch (event.type) {
    case 'phase_transition': {
      const key = `phase:${event.execution_api_id}:${event.phase}`;
      const existing = rows.get(key);
      if (event.status === 'completed') {
        const metrics = event.metrics ?? [];
        // Compared by VALUE, not reference: `event.metrics ?? []` mints a fresh array whenever the
        // field is absent, so a reference check would report every duplicate completion as a change
        // and re-publish a new snapshot on each one.
        //
        // The re-set is symmetric, which matters in both directions: a replayed event carrying counts
        // enriches a row an earlier bare event created, and a replayed BARE event would strip counts
        // off a populated row. The second only arises from an event persisted before metrics existed,
        // which we accept rather than guard — old data is out of scope — but it is the direction that
        // loses information, so anyone widening this arm should know it exists.
        if (
          _notNil(existing) &&
          existing.kind === 'phase' &&
          existing.state === 'done' &&
          sameMetrics(existing.metrics, metrics)
        ) {
          return false;
        }
        rows.set(key, { kind: 'phase', key, activityKey: event.phase, state: 'done', metrics });
        return true;
      }
      if (rows.has(key)) {
        return false; // idempotent: a duplicate started is a no-op
      }
      rows.set(key, { kind: 'phase', key, activityKey: event.phase, state: 'running', metrics: [] });
      return true;
    }
    case 'subagent_started': {
      const key = subagentKey(event.execution_api_id, event.subagent_name);
      if (rows.has(key)) {
        return false; // idempotent: a duplicate start is a no-op
      }
      rows.set(key, {
        kind: 'subagent',
        key,
        activityKey: event.subagent_name,
        state: 'running',
        parentKey: phaseKey(event.execution_api_id, event.phase),
        metrics: [],
      });
      return true;
    }
    case 'subagent_completed': {
      return setSubagentOutcome(
        rows,
        event.execution_api_id,
        event.subagent_name,
        'done',
        event.phase,
        event.duration_ms,
        event.metrics
      );
    }
    case 'subagent_failed': {
      return setSubagentOutcome(
        rows,
        event.execution_api_id,
        event.subagent_name,
        'failed',
        event.phase,
        event.duration_ms,
        [],
        event.content
      );
    }
    default:
      return false; // telemetry events don't produce Activity rows
  }
};

/**
 * The stable React key for a subagent row. Execution-scoped, so the same subagent in a later execution
 * of one session gets its own row, and `subagentName` needs no server-issued call id: each subagent is
 * a singleton the agent runs at most once per execution.
 */
const subagentKey = (executionApiId: AgentExecutionApiId, subagentName: string): string =>
  `subagent:${executionApiId}:${subagentName}`;

/**
 * The key of the phase row a subagent belongs under. Built the same way the `phase_transition` arm
 * builds its own key, so the two agree without either knowing about the other.
 */
const phaseKey = (executionApiId: AgentExecutionApiId, phase?: string | null): string | undefined =>
  _isNotBlank(phase) ? `phase:${executionApiId}:${phase}` : undefined;

/** Order-sensitive value comparison — metric order is contractual, so a reorder IS a change. */
const sameMetrics = (a: ReadonlyArray<ActivityMetric>, b: ReadonlyArray<ActivityMetric>): boolean =>
  a.length === b.length && a.every((metric, index) => metric.key === b[index].key && metric.value === b[index].value);

/**
 * Applies a subagent's terminal outcome. Creates the row if no `started` was seen (so a replayed or
 * dropped start can't hide a subagent that ran).
 *
 * Idempotent by VALUE, matching the phase arm: `connect` replays a session's Redis stream from the
 * start, so duplicate delivery is the normal case rather than an edge one, and a re-set that changed
 * nothing would publish a fresh snapshot and re-render the panel on every duplicate.
 *
 * `failed` is never downgraded to `done`. Replay ordering is not guaranteed, and a subagent that
 * reported an error did so — a later `completed` for the same name would otherwise turn a red mark
 * into a green tick.
 *
 * `durationMs` and `error` accept `null` as well as `undefined` because that is the wire shape
 * (`duration_ms?: number | null`, nullable `content`); normalising the two into the row's plain
 * optionals is part of this function's job, so no caller has to.
 */
const setSubagentOutcome = (
  rows: ActivityRowMap,
  executionApiId: AgentExecutionApiId,
  subagentName: string,
  state: 'done' | 'failed',
  phase?: string | null,
  durationMs?: number | null,
  metrics?: ReadonlyArray<ActivityMetric> | null,
  error?: string | null
): boolean => {
  const key = subagentKey(executionApiId, subagentName);
  const existing = rows.get(key);
  const parentKey = phaseKey(executionApiId, phase);
  const next: ActivityRow = {
    kind: 'subagent',
    key,
    activityKey: subagentName,
    state,
    // Kept from the row a `started` created when this event carries no phase, so an outcome
    // persisted before the server sent one cannot un-nest a row that was correctly nested.
    parentKey: parentKey ?? (existing?.kind === 'subagent' ? existing.parentKey : undefined),
    metrics: metrics ?? [],
    durationMs: _notNil(durationMs) ? durationMs : undefined,
    error: _notNil(error) ? error : undefined,
  };

  if (_notNil(existing) && existing.kind === 'subagent') {
    if (existing.state === 'failed' && state === 'done') {
      return false;
    }
    if (
      existing.state === state &&
      existing.durationMs === next.durationMs &&
      existing.error === next.error &&
      existing.parentKey === next.parentKey &&
      sameMetrics(existing.metrics, next.metrics)
    ) {
      return false;
    }
  }

  rows.set(key, next);
  return true;
};

/**
 * Materialises the accumulated rows into the ordered array the panel renders, applying the one
 * status-dependent transform: when the session is terminal, any row still `running` (its completion
 * never arrived) becomes `resolved` — a neutral "ended" state, not a false tick and not a false
 * failure. A row that reported `failed` is left alone. The stored map is untouched (resolution is
 * derived, not persisted), so it stays correct if more events arrive.
 */
export const toActivitySnapshot = (rows: ActivityRowMap, terminal: boolean): Array<ActivityRow> =>
  [...rows.values()].map((row) => (terminal && row.state === 'running' ? { ...row, state: 'resolved' } : row));

/** Whether a session status means the run has finished (drives terminal row resolution). */
export const isTerminalStatus = (status: AgentSessionStatus | undefined): boolean =>
  _notNil(status) && TERMINAL_SESSION_STATUSES.has(status);

/**
 * One phase and the units that ran inside it.
 *
 * A subagent whose `parentKey` names no row in this snapshot stays top level rather than being
 * dropped — events can be replayed from before the server sent a phase at all.
 *
 * Where the phase row is present but arrived after its own units, its units still nest. That is the
 * shape a lost `started` produces, and the server's phase emit deliberately swallows a failed
 * listener, so it is a real ordering rather than a hypothetical one.
 */
export interface ActivityGroup {
  row: ActivityRow;
  children: Array<ActivityRow>;
}

/**
 * Folds the flat snapshot into parents with their children, preserving first-appearance order at both
 * levels. Grouped here rather than in the panel so the panel stays layout and wording only.
 */
export const groupActivityRows = (rows: Array<ActivityRow>): Array<ActivityGroup> => {
  // Registered in full before any child is attached, so a lookup cannot depend on where its target
  // sits in the snapshot. Attaching during a single pass silently flattens a whole phase whenever
  // its row lands after the units that name it.
  const groups = new Map<string, ActivityGroup>(rows.map((row) => [row.key, { row, children: [] }]));
  const ordered: Array<ActivityGroup> = [];

  rows.forEach((row) => {
    const parent = row.kind === 'subagent' && _notNil(row.parentKey) ? groups.get(row.parentKey) : undefined;

    if (_notNil(parent)) {
      parent.children.push(row);
      return;
    }

    const group = groups.get(row.key);
    if (_notNil(group)) {
      ordered.push(group);
    }
  });

  return ordered;
};
